// **Benutzer** der Instanz (und Impersonation). Die Lizenz-Belegung steht hier
// zum Lesen; zugewiesen wird in der Instanz (Benutzer → Lizenz).

import { useQuery } from "@tanstack/react-query";
import { get } from "../../api/client";
import { keys } from "../../api/queries";
import type { InstanceUser, InstanceUsersResponse } from "../../api/types";
import { fmtDay } from "../../lib/format";
import { errorText } from "../../lib/labels";
import { Link, useSearchParams } from "../../router/router";
import { Badge, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { AddUser } from "./add-user";
import { loginAs } from "./login-as";
import type { TabProps } from "./tabs";
import { seatsOn as seatsOnOf } from "./users-helpers";

function UserState({ u, signIn }: { u: InstanceUser; signIn?: string }) {
  if (u.banned) return <Badge tone="error">gesperrt</Badge>;
  if (!u.approved) return <Badge tone="warn">wartet</Badge>;
  if (u.signed_in === false) return <Badge tone="info">{signIn === "local" ? "Einladung offen" : "noch nicht angemeldet"}</Badge>;
  return <Badge tone="ok">freigeschaltet</Badge>;
}

export function UsersTab({ tenant: t }: TabProps) {
  const adding = Boolean(useSearchParams().get("add"));
  const q = useQuery({ queryKey: keys.tenantUsers(t.id), queryFn: () => get<InstanceUsersResponse>(`/tenants/${t.id}/users`) });
  const here = `/tenants/${t.id}/users`;
  if (q.isPending) return <Card title="Benutzer"><Note>Wird geladen …</Note></Card>;
  if (q.isError) return <Card title="Benutzer"><Note tone="error">{errorText(q.error)}</Note></Card>;
  const data = q.data;
  if (adding) return <AddUser tenant={t} data={data} here={here} />;
  const { users } = data;
  const seatsOn = seatsOnOf(data);
  const seatName = (key: string) => data.seats.find((x) => x.key === key)?.name ?? `${key} (unbekannt)`;
  return (
    <Card
      title={`${users.length} Benutzer`}
      hint={<>
        „Impersonieren“ öffnet die Instanz mit den Augen dieses Benutzers. Der Kunde sieht es in seinem Audit-Log, die Plattform in ihrem.
        {seatsOn && <><br />Lizenzen: {data.seats.map((x) => `${x.name} ${x.used}/${x.count}`).join(" · ") || "keine Plätze"} — zugewiesen wird in der Instanz (Benutzer → Lizenz).</>}
      </>}
      actions={<Link className="pf-btn pf-btn--sm pf-btn--primary" to={`${here}?add=1`}>Benutzer hinzufügen</Link>}
      flush
    >
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead><tr><th>Benutzer</th><th>Rolle</th>{seatsOn && <th>Lizenz</th>}<th>Zustand</th><th className="pf-hide-phone">Seit</th><th></th></tr></thead>
          <tbody>
            {users.length ? users.map((u) => (
              <tr key={u.id}>
                <td><strong>{u.name || u.email}</strong>{u.name ? <div className="pf-sub">{u.email}</div> : null}</td>
                <td>{u.admin ? <Badge tone="info">Admin</Badge> : (u.role_name ?? "—")}</td>
                {seatsOn && <td>{u.license_key ? seatName(u.license_key) : <Badge tone="warn">keine</Badge>}</td>}
                <td><UserState u={u} signIn={data.sign_in} /></td>
                <td className="pf-hide-phone pf-sub">{fmtDay(u.created_at)}</td>
                <td className="pf-num">
                  <button type="button" className="pf-btn pf-btn--sm" disabled={u.banned} onClick={() => void loginAs(t.id, "impersonate", u.email)}>Impersonieren</button>
                </td>
              </tr>
            )) : <tr><td colSpan={5} className="pf-empty">Keine Benutzer</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
